/**
 * OpenTelemetry Web SDK initialisation (E1 + E2).
 *
 * Call `initTelemetry()` once from the SPA entry point **before** React renders.
 * After that, any module can call `getTracer(name)` to create spans.
 *
 * ## Environment variables (all optional)
 *
 * | Variable                            | Effect                                         |
 * | ----------------------------------- | ---------------------------------------------- |
 * | `VITE_OTEL_DISABLED=true`           | Skip SDK initialisation entirely (no-op).      |
 * | `VITE_OTEL_EXPORTER_OTLP_ENDPOINT` | OTLP/HTTP collector URL for trace export.      |
 * |                                     | When unset the provider is registered but no   |
 * |                                     | spans are exported (safe default for local dev).|
 *
 * ## Scope
 *
 * E1: registers the `WebTracerProvider` with a `BatchSpanProcessor`.
 * E2: enables fetch auto-instrumentation with `traceparent` propagation
 *     restricted to same-origin `/api/*` requests only.  Also registers
 *     `DocumentLoadInstrumentation` for a root navigation span.
 */

import { trace, type Tracer } from "@opentelemetry/api";
import {
  CompositePropagator,
  W3CTraceContextPropagator,
  W3CBaggagePropagator,
} from "@opentelemetry/core";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { Resource } from "@opentelemetry/resources";
import { BatchSpanProcessor, type SpanProcessor } from "@opentelemetry/sdk-trace-base";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";
import { ZoneContextManager } from "@opentelemetry/context-zone";

import { OTEL_DISABLED, OTEL_EXPORTER_OTLP_ENDPOINT } from "@/env";
import { UserContextSpanProcessor } from "./user-context-processor";

// Re-export user-context setters so consumers can import from "@/telemetry".
export { setUserId as setTelemetryUserId, clearUserId as clearTelemetryUserId } from "./user-context-processor";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Service name attached to every span emitted by the web app. */
const SERVICE_NAME = "floline-web";

/**
 * App version injected at build time by Vite (`define` in vite.config.ts).
 * Falls back to "0.0.0-dev" when the define is missing (e.g. in tests).
 */
const SERVICE_VERSION: string =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0-dev";

/**
 * Regex that matches same-origin `/api/` URLs.
 *
 * Because the app uses **relative** fetch URLs (e.g. `/api/entries`), the
 * browser resolves them against `window.location.origin`.  The
 * `FetchInstrumentation` receives the fully-resolved URL, so we match
 * against the current origin followed by `/api/`.
 *
 * This ensures `traceparent` is **never** sent to third-party origins.
 */
function buildApiPropagationPattern(): RegExp {
  // In a browser context `window.location.origin` is always available.
  // During SSR / tests it may not be, so we fall back to a safe pattern
  // that only matches relative `/api/` paths (which the instrumentation
  // resolves against the document origin anyway).
  if (typeof window !== "undefined" && window.location?.origin) {
    // Escape special regex chars in the origin (e.g. the dots in the hostname).
    const escaped = window.location.origin.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );
    return new RegExp(`^${escaped}/api/`);
  }
  // Fallback: match relative /api/ paths (resolved URLs always start with http(s)://).
  return /^https?:\/\/[^/]+\/api\//;
}

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let provider: WebTracerProvider | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialise the OpenTelemetry Web SDK.
 *
 * Safe to call multiple times — subsequent calls are no-ops.
 * When `VITE_OTEL_DISABLED` is `"true"` the function returns immediately.
 */
export function initTelemetry(): void {
  // Guard: already initialised or explicitly disabled.
  if (provider || OTEL_DISABLED) {
    return;
  }

  // --- Resource ----------------------------------------------------------

  const resource = new Resource({
    [ATTR_SERVICE_NAME]: SERVICE_NAME,
    "service.version": SERVICE_VERSION,
    "deployment.environment.name": import.meta.env.MODE,
  });

  // --- Span processors ---------------------------------------------------

  // UserContextSpanProcessor stamps `user.id` on every span at creation
  // time.  It must be first so the attribute is present before any other
  // processor (e.g. the BatchSpanProcessor) sees the span.
  const spanProcessors: SpanProcessor[] = [new UserContextSpanProcessor()];

  if (OTEL_EXPORTER_OTLP_ENDPOINT) {
    const exporter = new OTLPTraceExporter({
      url: OTEL_EXPORTER_OTLP_ENDPOINT,
    });

    spanProcessors.push(
      new BatchSpanProcessor(exporter, {
        // Sensible defaults for a browser environment:
        maxQueueSize: 100,
        maxExportBatchSize: 10,
        scheduledDelayMillis: 5_000,
        exportTimeoutMillis: 30_000,
      })
    );
  }

  // --- Provider -----------------------------------------------------------

  provider = new WebTracerProvider({
    resource,
    spanProcessors,
  });

  // Register the provider globally with:
  // - ZoneContextManager: enables async context propagation via zone.js
  //   so that child spans created inside Promises/setTimeout/etc. are
  //   correctly parented.
  // - CompositePropagator: injects both W3C Trace Context (`traceparent`)
  //   and W3C Baggage headers into outgoing requests.
  provider.register({
    contextManager: new ZoneContextManager(),
    propagator: new CompositePropagator({
      propagators: [
        new W3CTraceContextPropagator(),
        new W3CBaggagePropagator(),
      ],
    }),
  });

  // --- Instrumentations (E2) ---------------------------------------------

  const apiPropagationPattern = buildApiPropagationPattern();

  registerInstrumentations({
    instrumentations: [
      // Auto-instrument all `fetch()` calls.
      new FetchInstrumentation({
        // Only propagate trace headers to same-origin /api/* URLs.
        // This prevents `traceparent` from leaking to third-party
        // services (CopilotKit cloud, Electric, Open-Meteo, etc.).
        propagateTraceHeaderCorsUrls: [apiPropagationPattern],

        // Clear PerformanceResourceTiming entries after capturing them
        // to avoid unbounded memory growth on long-lived SPAs.
        clearTimingResources: true,
      }),

      // Capture a root span for the initial page load with navigation
      // and resource timing data (TTFB, DOM processing, etc.).
      new DocumentLoadInstrumentation(),
    ],
  });
}

/**
 * Return a `Tracer` instance scoped to the given name.
 *
 * If telemetry is disabled a no-op tracer is returned (the OpenTelemetry API
 * handles this transparently when no provider is registered).
 */
export function getTracer(name: string): Tracer {
  return trace.getTracer(name);
}
